/**
 * QueueManager (010 US1, FR-001/002) — multiple render runs with bounded
 * concurrency, pause/resume/cancel/reorder, persisted across restarts.
 *
 * Drives the EngineBinding (004) per job and emits state changes + trace events
 * to a sink (the telemetry bridge wires this to the cloud). Persistence uses the
 * QueueStore (PlatformIO.localStore). On launch, in-flight jobs are restored and
 * marked `interrupted`.
 */
import type {
  BatchResult,
  DesignBatch,
  JobItemResult,
  JobRecord,
  JobStatus,
  QueueStore,
  ViewName,
} from "@genz/platform";
import type { EngineBinding, BatchProgress, MockupAssets } from "@genz/engine-bind";
import { runBatch, type RunBatchContext, type RunItem } from "../batch/run.js";
import type { RenderSettings } from "@genz/engine-bind";
import { PRIMARY_VIEW } from "../templates/views.js";
import { fetchWithTimeout, DOWNLOAD_TIMEOUT_MS, NO_HTTP_CACHE } from "../templates/timeout.js";
// TYPE-ONLY: `manager.ts` gains no DOM canvas, no PlatformIO and no runtime `transparent/`
// import — the conversion reaches it through the injected `runTransparent` port, the same
// shape `resolveMockup` and `resolveRun` already use.
import type { TransparentRow, TransparentRunSpec } from "../transparent/run.js";
// `transparent/paths.ts` is a LEAF module (it imports nothing), so this runtime import brings
// no canvas and no PlatformIO with it. It is here so the checkpoint key and the row name are
// derived by ONE function.
import { relPathOf } from "../transparent/paths.js";

/** The fields every job kind carries, whatever it is running. */
export interface EnqueueInputBase {
  /** Groups sibling jobs from one "Create Mock-up" run into a single queue batch entry. */
  batchId?: string;
  /** Human template name for this job (shown in the batch-details popup). */
  templateName?: string;
  /**
   * WS-H1: when set, this job renders ONLY these (design, view) items — the failed items of
   * a finished job (see {@link QueueManager.retryFailed}). Everything else in `batch` is left
   * intact so the retried design lands on the SAME deduped export path it failed at.
   */
  retryItems?: { designSlug: string; view: ViewName }[];
  /** WS-H1: the job id this one is retrying (kept on the JobRecord; purely informational). */
  retryOf?: string;
}

/**
 * A template mockup render — what a queue job was, and still is unless it says otherwise.
 *
 * `kind` is OPTIONAL on this arm, and the NAME `EnqueueInput` stays on it rather than moving to
 * the union, so every existing caller and all 64 existing `queue.test.ts` /
 * `queue-panel.test.tsx` tests compile and pass UNEDITED — that is WS-N's acceptance criterion,
 * not a convenience. The union is {@link AnyEnqueueInput}.
 */
export interface EnqueueInput extends EnqueueInputBase {
  kind?: "mockup";
  batch: DesignBatch;
  ctx: RunBatchContext;
  settings: RenderSettings;
  templateId: string;
  /**
   * WS-P / AUD-8 LB-7: does this template's PUBLISHED version declare a baked mockup?
   *
   * `true` means the cloud row carries baked-asset refs, so a render with no locally-resolved
   * bake is a SYNC failure, never a classic template — refuse instead of warping the design
   * onto the bare case. `false` means the version genuinely has no bake and the classic path
   * is correct. `undefined` = an older/restored job record with no opinion; it falls back to
   * `hasClassicGeometry`, i.e. exactly today's behaviour, so a resumed pre-WS-P job is not
   * retroactively failed.
   *
   * It lives on the MOCKUP arm, not on {@link EnqueueInputBase}: a Transparent conversion has
   * no template version, so it must never acquire an opinion about bakes.
   */
  bakeExpected?: boolean;
}

/** The explicit name for the mockup arm, for code that has both arms in view. */
export type MockupEnqueueInput = EnqueueInput;

/**
 * WS-N / WS-J deferred 1: a Transparent folder conversion as a queue job.
 *
 * It carries no template identity because there is none — this is a folder conversion, not a
 * catalog render. `EnqueueInput` was template-shaped, which is exactly why the Transparent tab
 * was the only long-running conversion in the app with no queue row, no pause and no retry.
 */
export interface TransparentEnqueueInput extends EnqueueInputBase {
  kind: "transparent";
  spec: TransparentRunSpec;
}

/** Anything the queue can be asked to run. */
export type AnyEnqueueInput = EnqueueInput | TransparentEnqueueInput;

export const isTransparent = (i: AnyEnqueueInput): i is TransparentEnqueueInput =>
  i.kind === "transparent";

/**
 * WS-P: the render refused because the template DECLARES a baked mockup this device does not
 * have. A distinct class purely so the catch below can stamp `failureCode` without re-parsing
 * the message — the message itself is unchanged and still the operator-facing text.
 */
class BakeMissingError extends Error {}

export interface QueueManagerOptions {
  engine: EngineBinding;
  store: QueueStore;
  tenantId: string;
  deviceId: string;
  concurrency?: number;
  /** Notified on any job state change (UI subscription + telemetry bridge). */
  onChange?: (jobs: JobRecord[]) => void;
  /** Trace events from the engine, forwarded to telemetry. */
  onTrace?: (jobId: string, phase: string, level: "info" | "warn" | "error", message: string, itemKey?: string) => void;
  /**
   * Per-item render progress for a running job. The queue store holds only
   * metadata; this lets the batch store mirror the live `completed/total` count
   * so the Gallery shows a progress bar before the run finishes.
   */
  onProgress?: (jobId: string, progress: BatchProgress) => void;
  /**
   * Task A4: the job's async stage. `"preparing"` is published before the resolveMockup
   * await (IndexedDB + AES-GCM decrypt of baked assets) so the queue row can explain the
   * 0%-with-no-progress gap; `"rendering"` fires on the first `onProgress` tick, when the
   * live bar takes over. Both fire only within the job's active window — never after a
   * terminal status clears the live row — so a late phase write can't resurrect it.
   */
  onPhase?: (jobId: string, phase: "preparing" | "rendering") => void;
  /**
   * Job completion with the FULL byte-carrying `BatchResult` (outputs + failures).
   * The queue store keeps the pixel-free `JobItemResult`s for telemetry/ordering;
   * the render BYTES reach the gallery/export through this callback only.
   */
  onResult?: (jobId: string, result: BatchResult) => void;
  /**
   * WS-O / D-36: forwarded from `runBatch`'s `onItem` (mockup arm) and from
   * `convertTransparentFiles`'s `onRow` (transparent arm) — ONE callback for BOTH, so the
   * write-through writer has a single input and the two arms cannot drift into two durability
   * stories. Wired in `app/bootstrap.ts` to `export/runWriter.ts`.
   *
   * ABSENT is not "skip quietly" for a DESKTOP host: `bootstrap.ts` always wires it, and a
   * desktop run whose writer port is missing is a build error, not a silent in-memory run.
   * It is legitimately absent only for the pure-`runBatch` unit tests, which is why the
   * option is optional at the type level and asserted at the wiring site.
   */
  onOutput?: (jobId: string, item: RunItem) => void | Promise<void>;
  /**
   * A WHOLE-JOB failure (the engine threw before any `onResult`). Without this the throw
   * was caught silently (job marked failed + a trace) but the batch store never learned,
   * so its outputs+failures never reached `expectedTotal` and the Render button hung on
   * "Rendering…" forever. The host wires this to surface the error and release the button.
   */
  onJobFailed?: (jobId: string, error: string, expectedItems: number) => void;
  /**
   * WS-H1: "this job is about to render N items the run total does not yet account for" — a
   * retry job's failed items, or a resumed job's un-rendered remainder. Wired to the batch
   * store (`addExpected`) so the aggregate bar and the Render button stay honest instead of
   * clamping the arriving outputs away.
   */
  onJobExpecting?: (jobId: string, addedItems: number) => void;
  /** Resolves the batch for a job at run time (web keeps bytes in memory). */
  resolveRun?: (jobId: string) => AnyEnqueueInput | undefined;
  /**
   * Spec 019 + Task B3: resolve a template version's baked layered-mockup assets (shell/
   * mask/generated bytes + profile) at render time, keyed by `versionId` AND `view`. A
   * multi-view template bakes each view separately, so the worker resolves every distinct
   * view up front (see {@link runJob}); when a view returns assets, its items render as a
   * LAYERED MOCKUP via the engine compositor. Injected by the host (backed by the local
   * mockup store + cloud refs); null → classic render for that view.
   */
  resolveMockup?: (versionId: string, view: string) => Promise<MockupAssets | null>;
  /**
   * Resolve a view's CASE PHOTO to bytes for the CLASSIC (no-bake) render — the pixels behind
   * `base_case_image_ref`, which the pixel-free wire cannot carry (Constitution VII).
   *
   * Injected for the same reason `resolveMockup` is: `manager.ts` owns no storage and no fetch.
   * `app/bootstrap.ts` wires it to `stores/caseImage.ts` (hosted ref → this device's local image
   * store). Called ONLY for the views that are actually taking the classic arm, and only after
   * the bake resolution above has decided which those are.
   *
   * `null` (or an absent port) ⇒ the design-only render, exactly as before. It never throws and
   * it never fails a job: a template with no photo anywhere is still a template that must render.
   */
  resolveBaseImage?: (
    templateId: string,
    versionId: string,
    view: string,
    ref: string | null,
  ) => Promise<Uint8Array | null>;
  /**
   * WS-ERR F-8 — the queue's records are not reaching disk. Fired ONCE per session with the
   * store's reason. `app/bootstrap.ts` wires it to the queue store so `QueuePanel` can say it;
   * the job trace still carries it too, for the run's own history.
   */
  onPersistFailed?: (reason: string) => void;
  /**
   * WS-H1 (AUD-3 IM-6 / F-48): may a view with no local bake be composited over the PRIMARY
   * view's shell? A GETTER, not a value, so flipping the Settings toggle takes effect on the
   * next job without rebuilding the manager. Absent → `false` (fail loud instead of exporting
   * a plausible-looking wrong shell).
   */
  allowPrimaryViewFallback?: () => boolean;
  /**
   * WS-N: run ONE transparent conversion. Injected (not imported) so `manager.ts` gains no DOM
   * canvas, no `PlatformIO` and no runtime `transparent/` import — the same shape
   * `resolveMockup` uses for baked assets. `app/bootstrap.ts` wires it to
   * `transparent/run.ts::convertTransparentFiles`, with one `export/runWriter.ts::RunWriter`
   * armed across the loop so each ok row is filed through `onOutput` as it is keyed
   * (WS-O / D-36) instead of the whole set being saved when the job ends.
   *
   * ABSENT is not "skip quietly": a transparent job enqueued against a host that did not wire
   * this port FAILS LOUD with a named reason (AUD-3 §5.9). A silent `completed` with zero items
   * is the failure mode this codebase does not have.
   */
  runTransparent?: (
    jobId: string,
    spec: TransparentRunSpec,
    // WS-O / D-36: `onRow` may return a promise — the write-through writer is awaited here for
    // the same reason `runBatch` awaits `onItem`: an un-awaited sink lets the conversion
    // out-run the disk. A sync caller returning `undefined` is unaffected.
    hooks: { onRow(i: number, n: number, row: TransparentRow): void | Promise<void>; shouldAbort(): string | null },
  ) => Promise<{ rows: TransparentRow[]; savedTo?: string }>;
}

/** Does the template carry classic polygon geometry to fall back on? (AI-matte-flow
 *  templates carry baked assets only — for them, "no bake" means "cannot render at all".) */
function hasClassicGeometry(input: MockupEnqueueInput): boolean {
  const views = input.ctx.templateVersion?.payload?.views ?? [];
  return views.some((v: { masks?: unknown[] }) => (v.masks?.length ?? 0) > 0);
}

/**
 * May this job fall back to the classic warp-over-background render when no bake resolved?
 *
 * It used to be `hasClassicGeometry` — "does any view carry a mask?" — which is TRUE for every
 * MaskEditor/Wizard template (they all carry a `print_area` polygon). AUD-8 probe C: with that
 * predicate a device missing the bake rendered `completed`, with `mockup=undefined`, zero
 * traces, and 400 bare designs reached the customer. The polygon was never evidence of a
 * classic template; it is evidence of a print area.
 *
 * The declared kind wins: a version that PUBLISHED baked assets can only render from a bake.
 *
 * A version that published NO bake but carries a case photo still falls back to classic here —
 * and that fallback now COMPOSITES the photo. `runJob` resolves each classic view's
 * `base_case_image_ref` to bytes through `resolveBaseImage` and forwards them as
 * `RunBatchContext.baseImageByView`; `render::render_one_with_base` draws the design onto them.
 * The export is the product, not the artwork on a bare canvas.
 *
 * Keying `bakeExpected` on the case photo was tried and reverted, and must stay reverted:
 * `TemplateEditor.saveDraft` backfills that ref on every Save and every legacy classic row
 * already carries one, so it refused those rows on every device with no recovery — see the ledger
 * on `TemplateSelection.bakeExpected` (`stores/selection.ts`). The blank-mock-up class is closed
 * at its source (the wizard refuses to publish a bake it could not upload) and at the render.
 */
function mayFallBackToClassic(input: MockupEnqueueInput): boolean {
  if (input.bakeExpected === true) return false;   // declared layered → never classic
  return hasClassicGeometry(input);                // undefined/false → today's rule
}

function now(): number {
  return Date.now();
}

/** A status that must never be re-written by a later pause/cancel/completion path. */
function isTerminal(status: JobStatus): boolean {
  return status === "completed" || status === "failed" || status === "canceled";
}

export class QueueManager {
  private jobs = new Map<string, JobRecord>();
  private runs = new Map<string, AnyEnqueueInput>();
  private canceled = new Set<string>();
  private paused = new Set<string>();
  /**
   * WS-H1 / D-14: per job, the `"${designSlug}:${view}"` keys whose bytes have ALREADY been
   * published via `onResult` — the pause checkpoint. The resumed segment filters them out so
   * it continues at the next item instead of re-rendering from design #1 (AUD-3 LB-6).
   * Cleared when the job goes terminal.
   */
  private completedItems = new Map<string, Set<string>>();
  private active = new Set<string>();
  private concurrency: number;
  private readonly opts: QueueManagerOptions;

  constructor(opts: QueueManagerOptions) {
    this.opts = opts;
    this.concurrency = opts.concurrency ?? 1;
  }

  list(): JobRecord[] {
    return [...this.jobs.values()].sort((a, b) => a.position - b.position);
  }

  setConcurrency(n: number): void {
    this.concurrency = Math.max(1, n);
    this.pump();
  }

  /** Restore persisted jobs on launch; mark any non-terminal job interrupted. A job's runnable
   *  input (the design bytes) lives only in memory (`runs`), so nothing persisted can actually
   *  resume after a restart. Downgrading queued/running/paused → interrupted is critical: a
   *  stale "queued" job left as-is would be picked FIRST by `pump()`, and `runJob` early-returns
   *  on its missing input WITHOUT re-pumping — stalling the whole queue so a freshly-enqueued
   *  batch never starts and sits on "Queued" forever. */
  async restore(): Promise<void> {
    const persisted = await this.opts.store.all();
    for (const job of persisted) {
      if (job.status === "running" || job.status === "queued" || job.status === "paused") {
        job.status = "interrupted";
        // F-8: a bare `await store.save(job)` here would reject INTO `restore()`'s caller and
        // abandon the rest of the persisted jobs — on boot, which is the worst possible moment
        // to lose the queue. Report and carry on; the in-memory list below is still correct.
        try {
          await this.opts.store.save(job);
        } catch (e) {
          this.reportPersistFailure(job.jobId, e);
        }
      }
      this.jobs.set(job.jobId, job);
    }
    this.emit();
  }

  enqueue(input: AnyEnqueueInput): string {
    const jobId = `job-${now().toString(36)}-${this.jobs.size + 1}`;
    // WS-N: the per-kind half of the record, decided once. A transparent job has NO catalog
    // template — it is a folder conversion — so its identity fields are EMPTY rather than
    // synthetic: a made-up `templateId` is a lie every later reader of this JobRecord inherits
    // (the batch-details popup, the export path, the telemetry row). `runKind` is the honest
    // way to say what this job is. And no views: a file is not converted from N angles, so
    // `QueuePanel` branches on `runKind` instead of multiplying by an empty array.
    const shape = isTransparent(input)
      ? {
          templateId: "",
          templateVersionId: "",
          designCount: input.spec.files.length,
          views: [] as ViewName[],
          runKind: "transparent" as const,
        }
      : {
          templateId: input.templateId,
          templateVersionId: input.batch.versionId,
          designCount: input.batch.designs.length,
          views: input.batch.views,
          runKind: undefined,
        };
    const job: JobRecord = {
      jobId,
      tenantId: this.opts.tenantId,
      deviceId: this.opts.deviceId,
      templateId: shape.templateId,
      templateVersionId: shape.templateVersionId,
      engineVersion: this.opts.engine.engineVersion,
      // A retry job renders only its `retryItems`, so its row must count THOSE — a
      // "3 designs × 2 views" header over a 1-item retry is a lie the operator acts on.
      designCount: input.retryItems
        ? new Set(input.retryItems.map((i) => i.designSlug)).size
        : shape.designCount,
      views: input.retryItems && shape.runKind !== "transparent"
        ? [...new Set(input.retryItems.map((i) => i.view))]
        : shape.views,
      runKind: shape.runKind,
      batchId: input.batchId,
      templateName: input.templateName,
      retryOf: input.retryOf,
      status: "queued",
      queuedAt: now(),
      items: [],
      position: this.jobs.size,
    };
    this.jobs.set(jobId, job);
    this.runs.set(jobId, input);
    void this.persist(job);
    this.emit();
    this.pump();
    return jobId;
  }

  pause(jobId: string): void {
    this.paused.add(jobId);
    const job = this.jobs.get(jobId);
    if (job && (job.status === "queued" || job.status === "running")) {
      job.status = "paused";
      void this.persist(job);
      this.emit();
    }
  }

  resume(jobId: string): void {
    this.paused.delete(jobId);
    const job = this.jobs.get(jobId);
    if (job && job.status === "paused") {
      job.status = "queued";
      // The pause told the batch store to write off the un-rendered remainder so the Render
      // button could release. Resuming puts it back, or the aggregate bar clamps the
      // resumed segment's outputs away as they land.
      const input = this.runs.get(jobId) ?? this.opts.resolveRun?.(jobId);
      if (input) {
        const totalItems = input.retryItems
          ? input.retryItems.length
          : isTransparent(input)
            ? input.spec.files.length
            : input.batch.designs.length * new Set(input.batch.views).size;
        const remaining = Math.max(0, totalItems - (this.completedItems.get(jobId)?.size ?? 0));
        if (remaining > 0) this.opts.onJobExpecting?.(jobId, remaining);
      }
      void this.persist(job);
      this.emit();
      this.pump();
    }
  }

  cancel(jobId: string): void {
    this.canceled.add(jobId);
    const job = this.jobs.get(jobId);
    // Never overwrite a TERMINAL status. "completed" was already guarded; "failed" was not,
    // so cancelling after a job had already blown up relabelled the failure as a user
    // cancel and the real error disappeared from the queue row / telemetry.
    if (job && !isTerminal(job.status)) {
      job.status = "canceled";
      void this.persist(job);
      this.emit();
    }
  }

  /**
   * Re-run ONLY the failed items of `jobId` as a fresh job in the same batch row. AUD-3 LB-5:
   * a per-item throw was permanent and the only recovery was re-running all 500 designs.
   * `retryItems` keeps this from re-rendering (and re-writing) the items that succeeded.
   */
  retryFailed(jobId: string): string | undefined {
    const job = this.jobs.get(jobId);
    const input = this.runs.get(jobId) ?? this.opts.resolveRun?.(jobId);
    if (!job || !input) return undefined; // restored job: its design bytes are gone
    const failed = job.items
      .filter((i) => i.status === "failed")
      .map((i) => ({ designSlug: i.designSlug, view: i.view }));
    // Review-1 M-1: the failed set is CONSUMED by a successful retry (see
    // `reconcileRetrySource`), so a second click on a job that has nothing left to retry must
    // do nothing at all — enqueuing here re-rendered an item that had already succeeded and
    // landed a DUPLICATE output (and a duplicate export row). Say so in the trace rather than
    // returning a silent `undefined`: an operator clicking twice deserves an audit line.
    if (failed.length === 0) {
      this.opts.onTrace?.(jobId, "job", "info", `retryFailed: nothing to retry — every failed item of ${jobId} has since succeeded`);
      return undefined;
    }
    // Belt and braces for the same duplicate: a retry of this job that has NOT gone terminal is
    // already rendering these items. (The UI hides the button while the batch has work in
    // flight, but the manager must not depend on that.) Review-2 L-7: a retry-of-a-retry is
    // rendering this job's items too, so match the whole descendant chain, not just a child.
    const inFlight = [...this.jobs.values()].find(
      (j) =>
        (j.status === "queued" || j.status === "running" || j.status === "paused") &&
        this.retryAncestors(j).some((a) => a.jobId === jobId),
    );
    if (inFlight) {
      this.opts.onTrace?.(jobId, "job", "info", `retryFailed: ${inFlight.jobId} is already retrying the failed items of ${jobId}`);
      return undefined;
    }
    const newId = this.enqueue({ ...input, batchId: job.batchId ?? jobId, retryItems: failed, retryOf: jobId });
    this.opts.onJobExpecting?.(newId, failed.length);
    this.opts.onTrace?.(newId, "job", "info", `retrying ${failed.length} failed item${failed.length === 1 ? "" : "s"} from ${jobId}`);
    return newId;
  }

  /**
   * Review-1 M-1: write a retry job's outcome back onto the job it is retrying, so a
   * RECOVERED item stops counting as failed. Without this the source job kept its `failed`
   * row forever: after a successful retry the batch row still read "Retry N failed" with the
   * stale error, and a second click re-rendered the item — a duplicate output in the gallery
   * AND a duplicate row in the export, exactly what this wave promises never happens.
   *
   * An item that failed AGAIN keeps a `failed` row (with the newest error) and stays
   * retryable. Only `job.items` — the pixel-free queue record — is touched; the batch store's
   * outputs/failures accounting is left exactly as it was.
   *
   * Review-2 L-7: this walks the WHOLE `retryOf` chain, not one level. In root → r1 (fails
   * again) → r2 (succeeds), writing back only to r1 left the ROOT holding a `failed` row for
   * an item that had already succeeded — `retryFailed(root)` then rendered it a third time and
   * landed the duplicate output this wave exists to prevent.
   */
  private async reconcileRetrySource(retryJob: JobRecord, items: JobItemResult[]): Promise<void> {
    if (items.length === 0) return;
    const ancestors = this.retryAncestors(retryJob);
    for (const source of ancestors) {
      const byKey = new Map(source.items.map((i) => [`${i.designSlug}:${i.view}`, i]));
      // COPY the row (review-2 L-9): inserting the retry job's own item objects made the two
      // JobRecords share them, so any future in-place edit of one would rewrite the other.
      for (const i of items) byKey.set(`${i.designSlug}:${i.view}`, { ...i });
      source.items = [...byKey.values()];
      await this.persist(source);
    }
    if (ancestors.length > 0) this.emit();
  }

  /** The jobs `job` is (transitively) a retry of, nearest first. Cycle-guarded, and stops at
   *  the first ancestor the manager no longer holds. */
  private retryAncestors(job: JobRecord): JobRecord[] {
    const chain: JobRecord[] = [];
    const seen = new Set<string>([job.jobId]);
    let cur = job;
    while (cur.retryOf && !seen.has(cur.retryOf)) {
      seen.add(cur.retryOf);
      const parent = this.jobs.get(cur.retryOf);
      if (!parent) break;
      chain.push(parent);
      cur = parent;
    }
    return chain;
  }

  /** Move a job to a new 0-based position; others shift to keep order stable. */
  reorder(jobId: string, position: number): void {
    const ordered = this.list();
    const idx = ordered.findIndex((j) => j.jobId === jobId);
    if (idx === -1) return;
    const [moved] = ordered.splice(idx, 1);
    const clamped = Math.max(0, Math.min(position, ordered.length));
    ordered.splice(clamped, 0, moved);
    ordered.forEach((j, i) => {
      j.position = i;
      void this.persist(j);
    });
    this.emit();
  }

  private emit(): void {
    this.opts.onChange?.(this.list());
  }

  private async persist(job: JobRecord): Promise<void> {
    // Silent-failure sweep: every caller is `void this.persist(job)`, so a store failure used
    // to become an UNHANDLED REJECTION — the queue row on screen looked normal while nothing
    // durable backed it, and the run was unresumable after a restart with nothing said. The
    // job keeps running (the record is in memory and the outputs are written through), but the
    // loss of durability reaches the job's own trace, which is where the operator reads a run.
    // Once per session: a store that is down fails on every write, and one line per write is
    // the amplifier the release checklist names.
    try {
      await this.opts.store.save(job);
    } catch (e) {
      this.reportPersistFailure(job.jobId, e);
    }
  }

  /** F-8 — say it ONCE per session, on the job's own trace AND to the panel. A store that is
   *  down fails on every write, and one line per write is the amplifier the release checklist
   *  names. */
  private reportPersistFailure(jobId: string | null, e: unknown): void {
    if (this.persistFailed) return;
    this.persistFailed = true;
    const reason = e instanceof Error ? e.message : String(e);
    const sentence =
      `This run is not being saved to disk (${reason}). Rendering continues and files are ` +
      "still written to the save folder, but the queue cannot be resumed if the app closes.";
    if (jobId) this.opts.onTrace?.(jobId, "persist", "error", sentence);
    this.opts.onPersistFailed?.(reason);
  }

  /** Set once a queue-store write has failed, so the report above fires once per session
   *  rather than once per job state change. */
  private persistFailed = false;

  /** Start as many queued jobs as concurrency allows. */
  private pump(): void {
    if (this.active.size >= this.concurrency) return;
    const next = this.list().find(
      (j) => j.status === "queued" && !this.paused.has(j.jobId) && !this.active.has(j.jobId),
    );
    if (!next) return;
    void this.runJob(next.jobId);
    if (this.active.size < this.concurrency) this.pump();
  }

  private async runJob(jobId: string): Promise<void> {
    const job = this.jobs.get(jobId);
    if (!job) return;
    const input = this.runs.get(jobId) ?? this.opts.resolveRun?.(jobId);
    if (!input) {
      // No runnable input (e.g. a restored job whose in-memory design bytes are gone). Never
      // leave it "queued" — that stalls `pump()` (this job keeps being the next candidate but
      // can't run). Mark it interrupted, emit, and CONTINUE pumping so sibling/fresh jobs run.
      if (job.status !== "completed" && job.status !== "canceled") {
        job.status = "interrupted";
        await this.persist(job);
        this.emit();
      }
      this.pump();
      return;
    }
    if (this.canceled.has(jobId) || this.paused.has(jobId)) return;

    this.active.add(jobId);
    job.status = "running";
    job.startedAt = now();
    await this.persist(job);
    this.emit();

    // WS-N: the second arm. Inserted here, not woven in — everything below this line is
    // WS-H1's mockup path, unchanged.
    if (isTransparent(input)) return await this.runTransparentJob(jobId, job, input);

    // WS-H1: a retry job renders ONLY its `retryItems`; a RESUMED job skips whatever its
    // pause checkpoint already published. Both go through the one item-selection seam
    // (`RunBatchOptions.itemFilter`), above the engine, so the item boundary and the IPC
    // frame are untouched.
    const done = this.completedItems.get(jobId);
    const only = input.retryItems
      ? new Set(input.retryItems.map((i) => `${i.designSlug}:${i.view}`))
      : null;
    const itemFilter =
      only || done
        ? (slug: string, view: ViewName) => {
            const k = `${slug}:${view}`;
            return (!only || only.has(k)) && !done?.has(k);
          }
        : undefined;
    // What THIS SEGMENT actually plans to render — the number the batch store must write off
    // when the job aborts or dies whole. Using designs × views for a 1-item retry (or for a
    // resumed tail) would over-subtract `expectedTotal` and leave the Render button hanging.
    const totalItems = only ? only.size : input.batch.designs.length * new Set(input.batch.views).size;
    const plannedItems = Math.max(0, totalItems - (done?.size ?? 0));

    try {
      // Task A4: announce the "preparing" stage BEFORE the async asset resolution below
      // (brand-logo fetch + resolveMockup — IndexedDB read + AES-GCM decrypt of multi-MB
      // baked assets). The queue row shows "Preparing assets…" during this gap instead of a
      // silent 0% bar; it flips to "rendering" on the first render tick.
      this.opts.onPhase?.(jobId, "preparing");
      // Spec 017 US6: a persisted job stores only the brand-logo URL (bytes can't be
      // JSON-persisted); fetch the bytes here, at render time, so the engine can merge it.
      let ctx = input.ctx;
      if (ctx.brandLogoRef && !ctx.brandLogo) {
        try {
          const res = await fetchWithTimeout(ctx.brandLogoRef, NO_HTTP_CACHE, DOWNLOAD_TIMEOUT_MS, "Brand logo fetch");
          if (res.ok) {
            ctx = { ...ctx, brandLogo: new Uint8Array(await res.arrayBuffer()) };
          } else {
            // Was fully silent: a 404/403 logo produced a clean-looking mockup with no logo
            // and no trace. The render still proceeds — but leave an audit trail.
            this.opts.onTrace?.(jobId, "preparing", "warn", `brand logo fetch -> ${res.status} — rendering WITHOUT the logo (${ctx.brandLogoRef.slice(0, 64)})`);
          }
        } catch (e) {
          this.opts.onTrace?.(jobId, "preparing", "warn", `brand logo fetch failed (${e instanceof Error ? e.message : String(e)}) — rendering WITHOUT the logo`);
        }
      }
      // Spec 019 + Task B3: resolve baked layered-mockup assets PER VIEW so each rendered
      // item (design × view) composites over its OWN view's baked shell/ground. A multi-view
      // template bakes each view separately; resolving here (still under the "preparing"
      // phase) keeps the async IndexedDB reads + AES-GCM decrypts off the render tick. A
      // view with no bake falls back to the PRIMARY view's assets, then classic render.
      const resolveMockup = this.opts.resolveMockup;
      if (!ctx.mockup && !ctx.mockupByView && resolveMockup && input.batch.versionId) {
        const versionId = input.batch.versionId;
        // A resolveMockup that THROWS means the template's baked assets EXIST but couldn't be
        // loaded (IndexedDB miss / AES-GCM decrypt failure / corrupt ref) — rendering anyway would
        // silently DROP the product image from the export. That's the load-bearing distinction
        // from a clean `null`, which means the template legitimately has NO bake (a plain
        // warp-over-background template → the classic design-only render below is correct). We
        // used to swallow both into `null` via `.catch(() => null)`, so a resolve FAILURE looked
        // identical to "no bake" and produced a silently-wrong render. Track the two apart: capture
        // any throw so we can fail LOUD when nothing resolved because a bake couldn't be loaded.
        let resolveError: string | null = null;
        const tryResolve = async (view: string): Promise<MockupAssets | null> => {
          try {
            return await resolveMockup(versionId, view);
          } catch (e) {
            resolveError = e instanceof Error ? e.message : String(e);
            return null;
          }
        };
        // Resolve the primary view once and reuse the reference as the per-view fallback
        // (same object shared across views — bytes are never copied).
        const primary = await tryResolve(PRIMARY_VIEW);
        const byView: Record<string, MockupAssets | null> = {};
        let anyResolved = false;
        // WS-H1 (IM-6/F-48): compositing a view over the PRIMARY view's shell is
        // plausible-looking, reported as a success and auto-saved — so it is opt-in.
        const allowFallback = this.opts.allowPrimaryViewFallback?.() ?? false;
        const missingViews: string[] = [];
        for (const view of new Set(input.batch.views)) {
          const own = view === PRIMARY_VIEW ? primary : await tryResolve(view);
          if (!own && view !== PRIMARY_VIEW && primary) {
            missingViews.push(view);
            if (!allowFallback) {
              // Loud, not plausible: no assets for this view rather than the BACK view's shell.
              this.opts.onTrace?.(jobId, "preparing", "warn",
                `view "${view}" has no baked assets on this device — NOT rendering it over the "${PRIMARY_VIEW}" view's shell ` +
                `(Force sync the template, or enable the primary-view fallback in Settings → Rendering)`);
              byView[view] = null;
              continue;
            }
            // With the hatch open, keep the old behavior AND the old warn — plus who allowed
            // it, so a wrong-shell report is still diagnosable from the trace alone.
            this.opts.onTrace?.(jobId, "preparing", "warn",
              `view "${view}" has no baked assets on this device — rendering it over the "${PRIMARY_VIEW}" view's shell ` +
              `(allowed by Settings → Rendering; Force sync the template if this view was baked)`);
          }
          const assets = own ?? (allowFallback ? primary : null);
          byView[view] = assets;
          if (assets) anyResolved = true;
        }
        if (missingViews.length > 0 && !allowFallback && !mayFallBackToClassic(input)) {
          // No bake for these views AND no classic render is permitted for them — either the
          // template carries no polygon geometry at all, or (WS-P) its published version
          // DECLARES a baked mockup, which the polygon is not a substitute for. The only honest
          // outcomes are "fix the sync" or "opt in to the wrong shell". Say both.
          //
          // WS-P: name the version and the views explicitly BEFORE the throw. The generic catch
          // below traces the message, but not which version or which views — and "why is this
          // blank" is exactly the question the Error log could not answer (AUD-8 LB-8).
          this.opts.onTrace?.(jobId, "preparing", "error",
            `Refusing to render version ${versionId}: views [${missingViews.join(", ")}] have no baked assets on ` +
            `this device` + (input.bakeExpected === true
              ? ` and this template's published version DECLARES a baked mock-up, so the classic ` +
                `warp-over-background render would omit the product image`
              : ` and this template has no classic mask geometry to render them from`));
          throw new BakeMissingError(
            `These views have no baked assets on this device: ${missingViews.join(", ")} — and this template has no ` +
              `classic mask geometry to render them from. Use "Download missing" on the Batch tab (or Force sync on ` +
              `Templates), wait for the download, then render again. To export them over the "${PRIMARY_VIEW}" view's ` +
              `shell instead, turn on "Render a view with no baked assets over the primary view's shell" in Settings → Rendering.`,
          );
        }
        if (anyResolved) {
          ctx = { ...ctx, mockupByView: byView };
        } else if (resolveError) {
          // Expected a bake (a resolver threw) but NOTHING resolved → a design-only classic render
          // here would omit the product image and look "correct" to the queue. Fail the job LOUD
          // instead. Throwing routes through the catch below, which already marks the job failed,
          // traces the error (telemetry), and calls onJobFailed so the host surfaces it + releases
          // the Render button (rather than emitting a silently-wrong render).
          // WS-ERR H-1: a bake this device cannot LOAD is the same operator problem as one it
          // never downloaded — a broken IndexedDB (private window, blocked site data), a
          // decrypt failure after a cleared key, a corrupt row — and the remedy is identical.
          // Raised as a plain `Error` it got the generic danger row with no `failureCode`, so
          // `QueuePanel`'s named warn row, which is where that remedy is written, never
          // appeared. `BakeMissingError` is the same throw with the right name on it.
          throw new BakeMissingError(
            `Template's baked assets couldn't be resolved (${resolveError}) — export would omit the product image; refusing to render a design-only mockup. ` +
              `Fix: use "Download missing" on the Batch tab (or Force sync on Templates) to re-download its assets, then render again.`,
          );
        }
        // else: every view resolved a clean `null` (no throw) → nothing baked LOCALLY. If the
        // template also has no usable classic geometry (AI-matte-flow templates carry no
        // polygon masks — only baked assets), the classic fallback can't render it either:
        // fail LOUD with the recovery path instead of a TypeError or a design-only mockup.
        // This is the "renders on my machine, crashes on the other user's" case — their
        // device never hydrated the baked assets (interrupted sync, R2 CORS, fresh login).
        // WS-P widens this arm from "no polygon geometry" to "no classic render permitted": a
        // version that PUBLISHED a bake can only render from a bake, polygon or not. That is
        // AUD-8 probe C — the shape every real MaskEditor/Wizard template has.
        else {
          if (!mayFallBackToClassic(input)) {
            this.opts.onTrace?.(jobId, "preparing", "error",
              `Refusing to render version ${versionId}: no baked assets resolved on this device for ` +
              `view(s) [${[...new Set(input.batch.views)].join(", ")}]` + (input.bakeExpected === true
                ? ` and this template's published version DECLARES a baked mock-up, so the classic ` +
                  `warp-over-background render would export the design on a bare case`
                : ` and this template has no classic mask geometry to fall back on`));
            throw new BakeMissingError(
              "This template's baked assets aren't on this device yet, and it has no classic mask geometry to fall back on. " +
                "Use \"Download missing\" on the Batch tab (or Force sync on Templates), wait for the download, then render again.",
            );
          }
        }
      }
      // The classic (no-bake) arm: every view that got NO baked assets renders through
      // `render_one`, which until now drew the design on a bare canvas because nothing resolved
      // the view's `base_case_image_ref`. Resolve it here — still inside "preparing", once per
      // run rather than once per design — so the export carries the product.
      //
      // Only the views that are actually taking that arm: a view with a bake gets its product
      // from the bake, and downloading its photo as well would be megabytes for nothing.
      const resolveBaseImage = this.opts.resolveBaseImage;
      if (resolveBaseImage && ctx.templateVersion) {
        const wireViews = ctx.templateVersion.payload?.views ?? [];
        const byView: Record<string, Uint8Array | null> = {};
        let anyPhoto = false;
        for (const view of new Set(input.batch.views)) {
          if ((ctx.mockupByView?.[view] ?? ctx.mockup ?? null) !== null) continue; // baked
          const ref = wireViews.find((v) => v.view_type === view)?.base_case_image_ref ?? null;
          // The VERSION id travels with the template id so the resolver's memo cannot serve a
          // previous version's photo after a reference swap (see `stores/caseImage.ts`).
          // F-7: a THROWING port must not fail the run. This is the arm that exists so a
          // bake-less template still renders; a resolver that throws (a logger that blew up, a
          // platform transport that refused) has to degrade to the design-only export the branch
          // produced before the photo existed, not take the whole job down with it. Named in the
          // trace, then treated as "no photo".
          let bytes: Uint8Array | null = null;
          try {
            bytes = await resolveBaseImage(
              ctx.templateVersion.template_id,
              ctx.templateVersion.id,
              view,
              ref,
            );
          } catch (e) {
            this.opts.onTrace?.(jobId, "preparing", "warn",
              `case photo for view "${view}" could not be resolved ` +
              `(${e instanceof Error ? e.message : String(e)}) — rendering the design on a bare canvas`);
          }
          byView[view] = bytes;
          if (bytes) anyPhoto = true;
          // Say which way it went. "Why is this export a bare case?" was unanswerable from the
          // Error log, and the answer is always one of these two lines.
          this.opts.onTrace?.(jobId, "preparing", bytes ? "info" : "warn",
            bytes
              ? `classic render of view "${view}" will composite the template's case photo (${bytes.length} bytes)`
              : `classic render of view "${view}" has NO case photo on this device — exporting the design on a bare ` +
                `canvas. Re-open the template and save it so its reference photo is published, then render again.`);
        }
        if (anyPhoto) ctx = { ...ctx, baseImageByView: byView };
      }

      let renderingAnnounced = false;
      const result = await runBatch(this.opts.engine, input.batch, ctx, {
        settings: input.settings,
        itemFilter,
        /**
         * WS-O / D-36: the run's OUTPUT CHANNEL. Every finished item leaves through here, the
         * instant its bytes exist, on its way to the one writer that owns the run folder.
         * Awaited by `runBatch` — that await is the memory bound, not an optimisation.
         *
         * WS-O / O-4, and the ONE line that decides whether a killed run can be resumed
         * correctly: the D-14 checkpoint is recorded AFTER the write resolves, never before.
         * Recording first means a kill between the checkpoint and the write leaves a
         * checkpoint claiming a file that does not exist — and `resume` then SKIPS it, which
         * is a silently missing mockup, the exact class of bug this wave exists to end. A
         * sink that throws (a refused write) therefore does not check the item off either;
         * `runBatch` turns that into a `warn` naming the item, and the item stays in the
         * result so the run's own accounting still sees it.
         */
        onItem: async (item: RunItem) => {
          await this.opts.onOutput?.(jobId, item);
          if (item.kind !== "mockup") return;
          const seen = this.completedItems.get(jobId) ?? new Set<string>();
          seen.add(`${item.output.designSlug}:${item.output.view}`);
          this.completedItems.set(jobId, seen);
        },
        // Pause/Cancel now actually STOP the work: runBatch checks this between designs and
        // returns early. Previously the engine rendered the whole batch to completion and the
        // status was merely overwritten afterwards — the CPU/GPU stayed pinned, a paused job
        // came back "completed" (so `resume()`, which only revives a "paused" job, could never
        // restart it), and cancel gave no time back.
        shouldAbort: () =>
          this.canceled.has(jobId) ? "canceled" : this.paused.has(jobId) ? "paused" : null,
        onProgress: (p) => {
          // First tick = the "preparing" gap is over; flip the row to the live bar.
          if (!renderingAnnounced) {
            renderingAnnounced = true;
            this.opts.onPhase?.(jobId, "rendering");
          }
          this.opts.onProgress?.(jobId, p);
          this.emit();
        },
        onTrace: (e) => this.opts.onTrace?.(jobId, e.phase, e.level, e.message, e.itemKey),
      });

      if (result.aborted || this.canceled.has(jobId) || this.paused.has(jobId)) {
        // A PAUSED job keeps its "paused" status so `resume()` can revive it; a cancelled job
        // goes terminal — but never over an already-terminal status (a job that FAILED and is
        // then cancelled must stay "failed", or the real error is lost).
        const canceled = this.canceled.has(jobId) || result.aborted === "canceled";
        if (canceled && !isTerminal(job.status)) job.status = "canceled";
        else if (!canceled) job.status = "paused";
        // KEEP WHAT ALREADY RENDERED — for BOTH cancel and pause. Cancel has always kept its
        // outputs; pause used to throw them away because `resume()` re-ran the job from
        // design #1, so publishing here would have duplicated every one of them (AUD-3 LB-6:
        // a 400-design job paused at 390 re-rendered all 390). D-14 makes pause a CHECKPOINT:
        // the items published here are recorded in `completedItems` and the resumed segment
        // filters them out, so the publish is safe and the GPU time is not discarded.
        let kept = 0;
        if (result.outputs.length > 0) {
          const fresh: JobItemResult[] = result.outputs.map((o) => ({
            designSlug: o.designSlug,
            view: o.view,
            status: "done" as const,
            outputRef: o.localRef,
            contentHash: o.contentHash,
          }));
          // MERGE, don't replace: a resumed job's earlier segments are already in `items`.
          const byKey = new Map(job.items.map((i) => [`${i.designSlug}:${i.view}`, i]));
          for (const i of fresh) byKey.set(`${i.designSlug}:${i.view}`, i);
          job.items = [...byKey.values()];
          kept = fresh.length;
          // WS-O / O-4: the D-14 checkpoint is no longer written in bulk HERE, from whatever
          // rendered. It is written per item in `onItem` above, AFTER that item's bytes are on
          // disk. The two agree for every item that wrote; where they differ — an item that
          // rendered but whose write was REFUSED — the per-item rule is the correct one, and
          // the bulk add would have checked off a file that does not exist, so `resume` would
          // skip it. A canceled job's checkpoint is dropped by `settle`, as it always was.
          this.opts.onResult?.(jobId, result);
          // Whatever this retry segment DID render is recovered, even though the job stopped
          // early — take those items out of the source job's failed set (review-1 M-1).
          await this.reconcileRetrySource(job, fresh);
        }
        // An aborted job emits NEITHER onResult NOR onJobFailed, so the batch store's
        // expectedTotal was never reconciled: `rendering` stayed true for the rest of the
        // session ("Creating Mock-ups…" stuck forever) and auto-save never fired. Tell the
        // host to drop this job's expected items, exactly like the failure path does — minus
        // the `kept` ones, which the onResult above just added to `outputs` and would
        // otherwise be subtracted twice.
        this.opts.onJobFailed?.(
          jobId,
          canceled
            ? `Canceled — kept ${kept} mockup${kept === 1 ? "" : "s"} that had already rendered.`
            : `Paused — kept ${kept} mockup${kept === 1 ? "" : "s"}; Resume continues with the rest.`,
          Math.max(0, plannedItems - kept),
        );
      } else {
        const items: JobItemResult[] = result.outputs.map((o) => ({
          designSlug: o.designSlug,
          view: o.view,
          status: "done",
          outputRef: o.localRef,
          contentHash: o.contentHash,
        }));
        for (const f of result.failures) {
          items.push({ designSlug: f.designSlug, view: f.view, status: "failed", error: f.error });
        }
        // MERGE for the same reason the abort branch does: a RESUMED job already carries its
        // pre-pause segment's items, and replacing here would drop them from the queue row
        // (and from the per-item telemetry mirror) even though their bytes were published.
        // For a job that never paused, `job.items` is empty and this is a plain replace.
        const byKey = new Map(job.items.map((i) => [`${i.designSlug}:${i.view}`, i]));
        for (const i of items) byKey.set(`${i.designSlug}:${i.view}`, i);
        job.items = [...byKey.values()];
        job.status = result.failures.length > 0 && result.outputs.length === 0 ? "failed" : "completed";
        // Hand the FULL byte-carrying result to the batch store (gallery/export).
        // The queue store keeps only the pixel-free `items` above (telemetry-safe).
        this.opts.onResult?.(jobId, result);
        // A retry job's verdict is the source job's verdict for those items: recovered ones
        // leave its failed set (so the affordance goes away), re-failed ones stay retryable.
        await this.reconcileRetrySource(job, items);
      }
    } catch (err) {
      job.status = "failed";
      // WS-P: a NAMED failure state for the queue row. Additive on the record; the message is
      // unchanged, so nothing that reads the text is affected.
      if (err instanceof BakeMissingError) job.failureCode = "bake-missing";
      const message = err instanceof Error ? err.message : String(err);
      // Keep the existing error trace (telemetry) …
      this.opts.onTrace?.(jobId, "job", "error", message);
      // … AND tell the host so the UI can surface it and release the busy state (the throw
      // never produced an onResult, so the batch store would otherwise wait forever). Pass the
      // failed job's expected item count (what it PLANNED to render — a retry job plans only
      // its `retryItems`) so the store can drop it from the run total; otherwise, in a
      // multi-job run, its items keep aggregate progress short of expectedTotal forever and
      // the button never releases.
      this.opts.onJobFailed?.(jobId, message, plannedItems);
    } finally {
      await this.settle(jobId, job);
    }
  }

  /**
   * The end of EVERY job, whichever arm ran it. This is `runJob`'s `finally` body, extracted
   * verbatim in WS-N so the transparent arm cannot end differently from the mockup arm — the
   * timestamp rule, the checkpoint cleanup, the `active` release and the re-pump are one
   * implementation, not two.
   */
  private async settle(jobId: string, job: JobRecord): Promise<void> {
    // Only a TERMINAL job gets a completion timestamp. A paused job is still mid-run — it
    // resumes later — so stamping it "completed at" made the queue row read as finished.
    if (isTerminal(job.status)) {
      job.completedAt = now();
      job.durationMs = (job.completedAt ?? 0) - (job.startedAt ?? job.completedAt ?? 0);
      // A terminal job's pause checkpoint is dead weight — and for a CANCELED job it must
      // never gate a future `retryFailed`, which would otherwise filter out every item the
      // checkpoint recorded.
      this.completedItems.delete(jobId);
    }
    this.active.delete(jobId);
    await this.persist(job);
    this.emit();
    this.pump();
  }

  /**
   * WS-N / WS-J deferred 1 — the Transparent conversion as a queue job.
   *
   * ITEM-KEY CONVENTION, and the whole reason this arm needs no change to WS-H1's machinery:
   * a transparent item's `designSlug` is the SOURCE FILE'S RELATIVE PATH and its `view` is
   * `PRIMARY_VIEW`, a structural placeholder the UI never renders. With that, WS-H1's
   * `completedItems` checkpoint, `retryItems` filter and `reconcileRetrySource` all address
   * transparent items with the same `${designSlug}:${view}` key they use for mockups — none of
   * them was touched.
   *
   * It calls `onProgress`, `onPhase`, `onTrace`, `onJobExpecting`, `onJobFailed` and — per ok
   * row — `onOutput`. It does NOT call `onResult`: overlays are not gallery mockups and do not
   * belong in the batch store's outputs; their bytes reach WS-I's run folder through the same
   * write-through channel the mockup arm uses (WS-O / D-36), one overlay at a time.
   */
  private async runTransparentJob(
    jobId: string,
    job: JobRecord,
    input: TransparentEnqueueInput,
  ): Promise<void> {
    const key = (relPath: string) => `${relPath}:${PRIMARY_VIEW}`;
    try {
      this.opts.onPhase?.(jobId, "preparing");
      const run = this.opts.runTransparent;
      if (!run) {
        // Never a silent `completed` with zero items (AUD-3 §5.9).
        throw new Error("Transparent conversion is not available in this build");
      }
      // The SAME two filters the mockup arm builds, over the same keys: a retry job converts
      // only its `retryItems`, a RESUMED job skips whatever its pause checkpoint published.
      const done = this.completedItems.get(jobId);
      const only = input.retryItems ? new Set(input.retryItems.map((i) => `${i.designSlug}:${i.view}`)) : null;
      const files = input.spec.files.filter((f) => {
        const k = key(relPathOf(f));
        return (!only || only.has(k)) && !done?.has(k);
      });
      const plannedItems = files.length;

      let renderingAnnounced = false;
      const seen = this.completedItems.get(jobId) ?? new Set<string>();
      const fresh: JobItemResult[] = [];
      const { rows, savedTo } = await run(
        jobId,
        { ...input.spec, files },
        {
          onRow: (i, n, row) => {
            if (!renderingAnnounced) {
              renderingAnnounced = true;
              this.opts.onPhase?.(jobId, "rendering");
            }
            // AR-11: an ok row is filed as its `.png` output; the item key is its SOURCE path.
            const { source } = row;
            // A `skip` is a DONE item with a note, not an error — WS-I's log semantics, and
            // the warn line naming the file was already emitted by `convertTransparentFiles`.
            const item: JobItemResult =
              row.status === "fail"
                ? { designSlug: source, view: PRIMARY_VIEW, status: "failed", error: row.note }
                : { designSlug: source, view: PRIMARY_VIEW, status: "done", outputRef: row.relPath };
            fresh.push(item);
            if (row.status !== "fail") seen.add(key(source));
            if (row.status === "skip") {
              this.opts.onTrace?.(jobId, "converting", "warn", `${row.relPath} — ${row.note ?? "skipped"}`, key(source));
            }
            this.opts.onProgress?.(jobId, { completed: i, total: n });
            this.emit();
            // WS-O / D-36: the SAME output channel the mockup arm publishes through, so the
            // two arms cannot drift into two durability stories. Only an `ok` row has bytes —
            // a `skip` has none and a `fail` has none, and both already carry their own log
            // line from `convertTransparentFiles`. Handing the writer `undefined` here is how
            // a silently empty file gets written.
            if (row.status === "ok" && row.bytes && row.bytes.length > 0 && this.opts.onOutput) {
              return this.opts.onOutput(jobId, {
                kind: "transparent", source, relPath: row.relPath, bytes: row.bytes,
              });
            }
          },
          shouldAbort: () =>
            this.canceled.has(jobId) ? "canceled" : this.paused.has(jobId) ? "paused" : null,
        },
      );
      this.completedItems.set(jobId, seen);

      // MERGE, don't replace — a RESUMED job's earlier segment is already in `items`.
      const byKey = new Map(job.items.map((i) => [`${i.designSlug}:${i.view}`, i]));
      for (const i of fresh) byKey.set(`${i.designSlug}:${i.view}`, i);
      job.items = [...byKey.values()];

      const canceled = this.canceled.has(jobId);
      const paused = !canceled && this.paused.has(jobId);
      if (canceled || paused) {
        // Same posture as the mockup arm: KEEP what already converted, and tell the host to
        // write off only the remainder so no aggregate counter hangs.
        if (canceled && !isTerminal(job.status)) job.status = "canceled";
        else if (paused) job.status = "paused";
        if (!canceled) this.completedItems.set(jobId, seen);
        else this.completedItems.delete(jobId);
        this.opts.onJobFailed?.(
          jobId,
          canceled
            ? `Canceled — kept ${rows.length} overlay${rows.length === 1 ? "" : "s"} that had already converted.`
            : `Paused — kept ${rows.length} overlay${rows.length === 1 ? "" : "s"}; Resume continues with the rest.`,
          Math.max(0, plannedItems - rows.length),
        );
      } else {
        const failures = fresh.filter((i) => i.status === "failed").length;
        job.status = failures > 0 && failures === fresh.length ? "failed" : "completed";
        this.opts.onTrace?.(
          jobId,
          "job",
          failures > 0 ? "warn" : "info",
          `Transparent · ${fresh.length - failures} of ${fresh.length} converted${savedTo ? ` → ${savedTo}` : ""}`,
        );
        await this.reconcileRetrySource(job, fresh);
      }
    } catch (err) {
      job.status = "failed";
      const message = err instanceof Error ? err.message : String(err);
      this.opts.onTrace?.(jobId, "job", "error", message);
      this.opts.onJobFailed?.(jobId, message, input.spec.files.length);
    } finally {
      await this.settle(jobId, job);
    }
  }

  /** Status snapshot for a job (UI helper). */
  statusOf(jobId: string): JobStatus | undefined {
    return this.jobs.get(jobId)?.status;
  }
}
